import { setSharedToastSink } from '@flashka/shared/sync-client/internal/toast-sink';
import type { IconComponent, PressEvent } from '@flashka/shared/ui';
import { Toast } from '@flashka/shared/ui';

import { CircleAlertIcon, InfoIcon } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { type ExternalToast, toast as sonnerToast } from 'sonner';

import { playSound } from '@/lib/sound';

/**
 * Flashka web toast — a drop-in for `import { toast } from 'sonner'` that
 * routes sonner's typed toasts (error / success / warning / message / info)
 * through the cross-platform `@flashka/shared/ui` Toast card, so the web
 * presentation matches mobile + Figma.
 *
 * sonner stays the ENGINE (queue, positioning, auto-dismiss, lifecycle, swipe);
 * only the rendered card changes — each typed toast is emitted via
 * `toast.custom`. `custom`, `dismiss`, `loading`, and `promise` pass straight
 * through to sonner, so the bespoke `toast.custom` cards (celebration,
 * study-group, …) render exactly as before.
 */
type SharedVariant = 'default' | 'success' | 'critical';

/**
 * Options for the typed flashka toasts. Extends sonner's {@link ExternalToast}
 * but rebinds two fields to the shared Toast's own slots so call sites never
 * hand-roll a custom `<div>` message (which bypasses the card's variant text
 * colour + right-aligned action, the bug this shim exists to prevent):
 *   - `icon`   → the leading Toast icon slot — pass the icon COMPONENT (e.g.
 *                `Trash2Icon`), not a rendered element; the card injects the
 *                per-variant colour + size. (Replaces sonner's ReactNode icon.)
 *   - `action` → the trailing action Button (`actionLabel` + `onAction`). It
 *                auto-dismisses the toast after `onClick` — the Undo pattern,
 *                matching the mobile `emitToast` behaviour.
 * `position` is intentionally dropped: every DS toast renders in the global
 * top-center stack (see `<ToasterSonner>`); callers can no longer scatter them.
 * The ONE sanctioned exception is `anchor` below.
 */
export type FlashkaToastOptions = Omit<ExternalToast, 'icon' | 'action' | 'position'> & {
  icon?: IconComponent;
  /** Work in progress — the DS spinner replaces the icon (re-emit with the same `id` to resolve). */
  loading?: boolean;
  /**
   * Work in progress WITH a counter (0–100) — the DS bar under the title in
   * place of the description. Re-emit under the same `id` on every tick.
   */
  progress?: number;
  /**
   * Content identity for the DS card's morph: a re-emit under the same `id`
   * that CHANGES it blurs + fades the old content out and the new in
   * ("Uploading X…" → "X uploaded"); the same key updates in place.
   */
  contentKey?: string;
  /**
   * The onboarding upload toast rides bottom-right (decision 2026-09-08) —
   * the one exception to the top-center stack. Nothing else sets this.
   */
  anchor?: 'bottom-right';
  /** Re-emit without the knock — a progress tick updating a toast in place. */
  silent?: boolean;
  /** Extra classes on the DS card (a fixed width for the upload toast). */
  cardClassName?: string;
  /** Extra classes on the title text (`block truncate` to ellipsize a file name). */
  titleClassName?: string;
  action?: { label: string; onClick?: (event: PressEvent) => void };
  /**
   * Show the dismiss X. OFF by default: our toasts auto-dismiss after a few
   * seconds, so a manual close is chrome nobody needs — and it stole width
   * from the copy. Opt in only for a toast that does NOT auto-dismiss
   * (`duration: Infinity`) and has no action to close it.
   */
  dismissable?: boolean;
};

// Strip sonner's default wrapper chrome for OUR shared toasts (the card brings
// its own bg / border / shadow). Per-toast `classNames.toast` overrides the
// global `<ToasterSonner>` `classNames.toast`, so bespoke `toast.custom` cards
// keep the global styling untouched.
//
// `!w-full` (NOT `!w-auto`): sonner sets a width on the `<li>` only for its own
// styled toasts (`--width`), not for custom/unstyled ones. Our card is
// `w-full max-w-[…]`, which collapses to 0 inside an `auto`-width absolute li
// (circular). `!w-full` pins the li to the toaster width so the card fills it —
// otherwise the 0-width card renders as a tall sliver and the stack heights are
// mis-measured (the "toasts jump when one disappears" bug).
//
// Under 600px that full width is 16px too much: sonner lays the toaster out as
// `left: offset; right: offset; width: 100%`, i.e. a whole VIEWPORT pushed 16px
// right, and corrects for it on the toast itself — a correction this
// `!important` utility overrides. See the matching media query in `index.css`,
// which is where the fix has to live to out-specify this class.
// `!flex !justify-center`: the card hugs its copy, so without this it would pin
// LEFT inside the full-width row instead of sitting centred under the toaster
// column.
export const RESET_WRAPPER =
  '!flex !w-full !justify-center !border-0 !bg-transparent !p-0 !shadow-none';
// An `anchor`ed toast hugs the viewport corner instead: the li is still the
// toaster's full width, so centring the card would float it ~100px in from
// the edge — the card's distance from the right must equal sonner's own
// viewport offset, like its distance from the bottom.
const RESET_WRAPPER_END = RESET_WRAPPER.replace('!justify-center', '!justify-end');

function showShared(
  variant: SharedVariant,
  message: ReactNode,
  opts?: FlashkaToastOptions,
) {
  // Small dry knock for a toast appearing — ambient tier in the shared sound
  // engine: throttled to one knock/second and suppressed while any other
  // sound is still playing, so toast bursts never stack. `silent` re-emits
  // (progress ticks) skip it.
  if (!opts?.silent) playSound('toast.show');
  // `description` MUST be destructured out of `rest`: sonner renders its own
  // `<div data-description>` next to the custom jsx whenever `toast.description`
  // is set, so forwarding it printed the description twice — once inside the DS
  // card, once as bare text under it.
  // It can also be a render function; the shared Toast takes a plain ReactNode,
  // so drop the function form (no call site uses it).
  const {
    icon,
    loading,
    progress,
    contentKey,
    anchor,
    silent: _silent,
    cardClassName,
    titleClassName,
    action,
    dismissable = false,
    description: rawDescription,
    ...rest
  } = opts ?? {};
  const description = typeof rawDescription === 'function' ? undefined : rawDescription;
  return sonnerToast.custom(
    (id) => (
      <Toast
        variant={variant}
        size="md"
        icon={icon}
        loading={loading}
        progress={progress}
        contentKey={contentKey}
        title={message}
        description={description}
        className={cardClassName}
        titleClassName={titleClassName}
        actionLabel={action?.label}
        onAction={
          action
            ? (event) => {
                action.onClick?.(event);
                sonnerToast.dismiss(id);
              }
            : undefined
        }
        dismissable={dismissable}
        onDismiss={() => sonnerToast.dismiss(id)}
      />
    ),
    {
      ...rest,
      // Explicit even for the defaults: sonner keeps a re-emitted id's LAST
      // position, duration and dismissability (a dismissed toast's record
      // stays behind and the next emission merges over it), so a deck's
      // standard card after the onboarding's bottom-right, never-ending
      // upload toast would otherwise stay bottom-right and never dismiss.
      position: anchor ?? 'top-center',
      duration: rest.duration,
      dismissible: rest.dismissible ?? true,
      unstyled: true,
      closeButton: false,
      classNames: {
        ...opts?.classNames,
        toast: anchor ? RESET_WRAPPER_END : RESET_WRAPPER,
      },
    },
  );
}

/**
 * Route the sync client's toasts (persistence errors, op-rejected) through the
 * DS card. Without this they fall to shared's no-op sink; before the sink
 * existed they imported `sonner` directly and rendered sonner's OWN built-in
 * card — the red slab with a filled icon and a circular close button, which is
 * not the design system at all. Module scope so it is registered as soon as
 * anything imports the toast, i.e. before a sync socket can emit.
 */
const SHARED_TOAST_ICONS = { alert: CircleAlertIcon, info: InfoIcon } as const;

setSharedToastSink((variant, { title, description, duration, id, icon }) => {
  showShared(variant, title, {
    description,
    duration,
    id,
    icon: icon ? SHARED_TOAST_ICONS[icon] : undefined,
  });
});

export const toast = Object.assign(
  (message: ReactNode, opts?: FlashkaToastOptions) =>
    showShared('default', message, opts),
  {
    error: (message: ReactNode, opts?: FlashkaToastOptions) =>
      showShared('critical', message, opts),
    success: (message: ReactNode, opts?: FlashkaToastOptions) =>
      showShared('success', message, opts),
    // No amber "warning" in the design-system Toast — maps to critical (red),
    // matching the mobile sonner→native variant bridge.
    warning: (message: ReactNode, opts?: FlashkaToastOptions) =>
      showShared('critical', message, opts),
    info: (message: ReactNode, opts?: FlashkaToastOptions) =>
      showShared('default', message, opts),
    message: (message: ReactNode, opts?: FlashkaToastOptions) =>
      showShared('default', message, opts),
    /**
     * PERSISTENT toast, for follow nudges only.
     *
     * Everything else on this object auto-dismisses after a few seconds,
     * which is right for a receipt and wrong for an offer: a nudge asks the
     * user to do something, so it waits until they act on it, swipe it away,
     * or leave the screen. The caller gets the id back and is responsible for
     * calling `toast.dismiss(id)` when its screen unmounts — otherwise the
     * nudge outlives the thing it was about.
     *
     * No close button: the way out is the action or the swipe. `dismissible`
     * keeps sonner's swipe-to-dismiss on (the direction follows the toaster's
     * top-center position — sonner owns that, it is not per-toast).
     *
     * `onDismiss` fires on EVERY removal, the caller's own `toast.dismiss(id)`
     * included — sonner draws no distinction. A caller that needs to know the
     * user swiped has to flag its own programmatic dismissals.
     */
    nudge: (
      render: (id: string | number) => ReactElement,
      opts?: { onDismiss?: () => void },
    ): string | number => {
      playSound('toast.show');
      return sonnerToast.custom(render, {
        duration: Infinity,
        dismissible: true,
        unstyled: true,
        closeButton: false,
        classNames: { toast: RESET_WRAPPER },
        onDismiss: opts?.onDismiss ? () => opts.onDismiss?.() : undefined,
      });
    },
    // Pass-throughs — bespoke custom cards + lifecycle controls stay on sonner.
    // `custom` still knocks: celebration/SG/undo cards are toasts too.
    custom: ((...args: Parameters<typeof sonnerToast.custom>) => {
      playSound('toast.show');
      return sonnerToast.custom(...args);
    }) as typeof sonnerToast.custom,
    dismiss: sonnerToast.dismiss,
    loading: sonnerToast.loading,
    promise: sonnerToast.promise,
  },
);
